<template>
    <!-- ✅ 收合狀態：保留一顆「展開」按鈕 -->
    <div v-if="!open" class="me-collapsed">
      <button class="expand" type="button" @click.stop.prevent="$emit('open')">
        展開
      </button>
    </div>
  
    <!-- ✅ 展開狀態：遮罩 + sheet -->
    <div v-else class="mobile-overlay" @click.self="$emit('close')">
      <div class="mobile-editor" @click.stop>
        <div class="me-head">
          <div class="t">文字內容</div>
          <button class="x" type="button" @click.stop.prevent="$emit('close')">×</button>
        </div>
  
        <div class="me-body">
          <textarea v-model="selectedBlock.text" rows="3" />
  
          <div class="me-row">
            <div class="me-col">
              <div class="lbl">大小</div>
              <input type="range" min="50" max="200" step="1" v-model.number="selectedBlock.fontScale" />
            </div>
  
            <div class="me-col">
              <div class="lbl">顏色</div>
              <div class="color-row">
                <input
                  :key="selectedBlock.id"
                  type="color"
                  :value="selectedColor"
                  @input.stop="$emit('colorPick', $event)"
                  @change.stop="$emit('colorPick', $event)"
                />
                <input
                  class="hex"
                  type="text"
                  :value="hexDraft"
                  @blur="$emit('commitHex')"
                  @keydown.enter.prevent="$emit('commitHex')"
                />
              </div>
            </div>
          </div>
  
          <div class="me-actions">
            <button class="btn ghost" type="button" @click.stop.prevent="$emit('reset')">重置</button>
            <button class="btn primary" type="button" @click.stop.prevent="onApply">套用</button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script setup lang="ts">
  import type { OcrBlock } from '@/types/ainotebook'
  
  const props = defineProps<{
    open: boolean
    selectedBlock: OcrBlock
    selectedColor: string
    hexDraft: string
  }>()
  
  const emit = defineEmits<{
    (e: 'open'): void
    (e: 'close'): void
    (e: 'colorPick', ev: Event): void
    (e: 'commitHex'): void
    (e: 'reset'): void
    (e: 'apply'): void
  }>()
  
  function onApply() {
    // ✅ 避免 hex 還沒 commit 就套用
    emit('commitHex')
    emit('apply')
    // 收合交給父層 onMobileApply 做（applyEdits 跑完再收）
  }
  </script>
  
  <style scoped lang="scss">
  /* 桌面預設不顯示 */
  .mobile-overlay,
  .me-collapsed {
    display: none;
  }
  
  @media (max-width: 1100px) {
    /* ✅ 收合：右下角展開按鈕 */
    .me-collapsed {
      display: block;
      position: fixed;
      right: 12px;
      bottom: 12px;
      z-index: 999;
      pointer-events: auto;
  
      .expand {
        height: 40px;
        padding: 0 14px;
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.10);
        background: rgba(10, 18, 33, 0.85);
        backdrop-filter: blur(10px);
        color: #e8eefc;
        font-weight: 800;
        cursor: pointer;
      }
    }
  
    /* ✅ 展開：遮罩 + sheet */
    .mobile-overlay {
      display: flex;
      position: fixed;
      inset: 0;
      z-index: 999;
      background: rgba(0, 0, 0, 0.42);
      padding: 10px;
      align-items: flex-end;
      justify-content: center;
    }
  
    .mobile-editor {
      width: min(560px, 100%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.10);
      background: rgba(10, 18, 33, 0.92);
      backdrop-filter: blur(10px);
      overflow: hidden;
  
      .me-head {
        padding: 10px 12px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  
        .t { font-weight: 800; }
        .x {
          width: 30px;
          height: 30px;
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          background: rgba(255, 255, 255, 0.04);
          color: #e8eefc;
          cursor: pointer;
        }
      }
  
      .me-body {
        padding: 12px 16px;
        display: grid;
        gap: 12px;
  
        textarea {
          font-size: 14px;
          line-height: 1.15;
          padding: 8px 10px;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.10);
          background: rgba(255, 255, 255, 0.04);
          color: #e8eefc;
          outline: none;
        }
  
        .me-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
        }
  
        .lbl {
          font-size: 12px;
          color: rgba(232, 238, 252, 0.65);
          margin-bottom: 6px;
        }
  
        .color-row {
          display: grid;
          grid-template-columns: 44px 1fr;
          gap: 8px;
          align-items: center;
  
          input[type='color'] {
            width: 44px;
            height: 40px;
            padding: 0;
            border: none;
            background: transparent;
          }
  
          .hex {
            height: 40px;
            border-radius: 12px;
            border: 1px solid rgba(255, 255, 255, 0.10);
            background: rgba(255, 255, 255, 0.04);
            color: #e8eefc;
            padding: 0 10px;
            outline: none;
          }
        }
  
        .me-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding-top: 4px;
        }
      }
    }
  }
  
  .btn {
    height: 40px;
    padding: 0 14px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.10);
    background: rgba(255,255,255,0.05);
    color: #e8eefc;
    cursor: pointer;
    font-weight: 700;
  }
  .btn.primary {
    background: linear-gradient(180deg, rgba(29,123,255,1), rgba(29,123,255,0.85));
    border-color: rgba(29,123,255,0.55);
  }
  .btn.ghost { background: rgba(255,255,255,0.03); }
  </style>
  